/**
 * API Utility with Clerk JWT Authentication
 * Automatically adds Clerk session token to all API requests
 */

import axios from 'axios';

// Exported so components that need to build raw URLs (e.g. for fetch,
// window.open downloads, or react-pdf's <Document file={...} />) can share the
// single source of truth instead of hardcoding `http://localhost:8000` —
// which silently works in dev and then breaks every production deploy.
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

// Verbose per-request logging was previously always-on. It fires on every single
// request and was a measurable drag on the main thread (string serialization +
// DevTools overhead). Now gated behind REACT_APP_API_DEBUG=1.
const API_DEBUG = process.env.REACT_APP_API_DEBUG === '1';

// Create axios instance.
//
// Timeout rationale: 120s. The hosted backend (Render free tier) can take
// 30–60s to spin up from idle (cold start). The *first* authenticated request
// after a cold start additionally pays for JWKS fetch + Clerk metadata calls.
// The previous 60s value was tripping on project-creation after a cold start
// and surfacing as a confusing "AxiosError: timeout of 60000ms exceeded" even
// though the backend was still processing the request. Steady-state requests
// after the JWKS/metadata cache is warm return in <500ms, so this timeout
// only affects cold-start recovery.
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000,
});

// Store the getToken function from Clerk
let getTokenFn = null;

// Token cache: Clerk's getToken hits an internal cache, but every call still
// awaits a promise resolution. Caching per 50s avoids pointless work on bursts
// of concurrent requests (e.g. the dashboard firing 3 requests in parallel).
let cachedToken = null;
let cachedTokenExpiry = 0;

/**
 * Initialize API with Clerk session
 * Call this from App.js after Clerk is loaded
 */
export const initializeAPI = (getToken) => {
  getTokenFn = getToken;
  cachedToken = null;
  cachedTokenExpiry = 0;
};

const resolveToken = async () => {
  if (!getTokenFn) return null;
  const now = Date.now();
  if (cachedToken && now < cachedTokenExpiry) {
    return cachedToken;
  }
  const token = await getTokenFn();
  if (token) {
    cachedToken = token;
    // Clerk tokens live 60s; refresh 10s early to be safe
    cachedTokenExpiry = now + 50 * 1000;
  }
  return token;
};

// Request interceptor to add JWT token
api.interceptors.request.use(
  async (config) => {
    try {
      const token = await resolveToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else if (API_DEBUG) {
        console.warn('No token available for request:', config.url);
      }
    } catch (error) {
      if (API_DEBUG) console.error('Failed to get Clerk token:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Invalidate cached token so the next request re-fetches
      cachedToken = null;
      cachedTokenExpiry = 0;
      if (API_DEBUG) console.warn('401 Unauthorized:', error.config?.url);
    }
    return Promise.reject(error);
  }
);

export default api;
